import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/billing.service';

interface ManageSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManageSubscriptionModal: React.FC<ManageSubscriptionModalProps> = ({ isOpen, onClose }) => {
  const { user, token, updateUser } = useAuth();
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !user) return null;

  const FREE_LIMIT = 50 * 1024 * 1024; // 50MB
  const isOverFreeLimit = user.storageUsed > FREE_LIMIT;
  
  const formatBytes = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2) + ' MB';

  const handleDowngrade = async () => {
    if (!token) return;
    const confirmMessage = user.tier === 'TEAM' 
        ? "Are you sure? Downgrading will cancel your Team subscription and disband the organization. All members will be converted to Free accounts."
        : "Are you sure you want to cancel your Pro subscription and downgrade to Free?";

    if (!window.confirm(confirmMessage)) return;

    setIsDowngrading(true);
    setError(null);
    try {
      await billingService.downgradePlan(token);
      updateUser({ tier: 'FREE', organizationId: null, orgRole: null });
      alert("Successfully downgraded to the Free plan.");
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to downgrade plan.');
    } finally {
      setIsDowngrading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Manage Subscription</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        <div className="p-6 bg-gray-50">
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Current Plan</h3>
            <div className="flex items-center justify-between bg-white p-4 rounded border border-gray-200 shadow-sm">
                <div>
                    <span className="font-bold text-lg text-gray-900">{user.tier} Plan</span>
                    <p className="text-sm text-gray-500">
                        {user.tier === 'PRO' ? '$3.00 / month' : user.tier === 'TEAM' ? '$5.00 / user / month' : 'Free forever'}
                    </p>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full uppercase">
                    Active
                </span>
            </div>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm border border-red-200 rounded">{error}</div>}

          {user.tier !== 'FREE' && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Downgrade Options</h3>
              
              {isOverFreeLimit && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded text-sm text-yellow-800">
                      <strong>⚠️ Storage Warning:</strong> You are currently using {formatBytes(user.storageUsed)}. The Free plan only allows {formatBytes(FREE_LIMIT)}. 
                      <br /><br />
                      If you downgrade now, your account will be <strong>Over Quota</strong>. You will not be able to add new data or create new spreadsheets until you delete existing files to get under the 50 MB limit.
                  </div>
              )}

              <div className="bg-white p-4 rounded border border-gray-200 shadow-sm">
                  <p className="text-sm text-gray-600 mb-4">
                      Downgrading to the Free plan will stop all future monthly charges immediately. You will lose access to premium features and increased storage limits.
                  </p>
                  <button 
                      onClick={handleDowngrade} 
                      disabled={isDowngrading}
                      className="w-full py-2 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700 font-bold rounded transition-colors"
                  >
                      {isDowngrading ? 'Processing...' : 'Cancel Subscription (Downgrade to Free)'}
                  </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};